import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  Popover,
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import DescriptionIcon from "@mui/icons-material/Description";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";

const VehicleTrendGrid = ({
  city,
  vehicleId,
  filters,
  vehicleTrendGridApi,
  setVehicleTrendGridApi,
  totalCount,
  setTotalCount,
  getVehicleTrendData,
}) => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  // const [vehicleTrendGridApi, setVehicleTrendGridApi] = useState([]);
  const [columns, setColumns] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 8,
  });
  // const [totalCount, setTotalCount] = useState(0);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [columnAnchorEl, setColumnAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const filterOpen = Boolean(filterAnchorEl);
  const columnOpen = Boolean(columnAnchorEl);

  // Column visibility state - will be initialized after cols definition
  const [visibleColumns, setVisibleColumns] = useState({});

  // Filter state
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilters, setActiveFilters] = useState([]);

  // Filter operators
  const operators = [
    { value: "contains", label: "Contains" },
    { value: "notContains", label: "Does not contain" },
    { value: "equals", label: "Equals" },
    { value: "startsWith", label: "Starts with" },
    { value: "endsWith", label: "Ends with" },
    { value: "isEmpty", label: "Is empty" },
    { value: "isNotEmpty", label: "Is not empty" },
  ];

  // Filter selection state
  const [selectedFilterColumn, setSelectedFilterColumn] = useState("");
  const [selectedOperator, setSelectedOperator] = useState("contains");
  const [filterValue, setFilterValue] = useState("");

  function createData(
    id,
    name,
    licensePlate,
    totalBookings,
    peakBookingDays,
    revenue,
    maintenance,
    fuel,
    total,
    netProfit,
    revenuePerDay,
    costPerDay,
    breakEvenDays,
    status,
  ) {
    return {
      id,
      name,
      licensePlate,
      totalBookings,
      peakBookingDays,
      revenue,
      maintenance,
      fuel,
      total,
      netProfit,
      revenuePerDay,
      costPerDay,
      breakEvenDays,
      status,
    };
  }

  // Define cols before using it
   // Add copy function
  const copyVehicleNumber = (licensePlate) => {
    if (!licensePlate || licensePlate === "-") {
      toast.error("No vehicle number to copy", { 
        position: "bottom-center",
        style: { fontFamily: "Outfit", fontSize: "14px" }
      });
      return;
    }
    
    navigator.clipboard.writeText(licensePlate)
      .then(() => {
        toast.success("Vehicle number copied!", { 
          position: "bottom-center",
          style: { fontFamily: "Outfit", fontSize: "14px" }
        });
      })
      .catch(() => {
        toast.error("Failed to copy vehicle number", { 
          position: "bottom-center",
          style: { fontFamily: "Outfit", fontSize: "14px" }
        });
      });
  };

  // Define cols before using it
  const cols = [
    {
      field: "name",
      headerName: "Vehicle Name",
      minWidth: 200,
      flex: 1,
    },
        {
      field: "licensePlate",
      headerName: "Vehicle No.",
      minWidth: 220,
      flex: 1,
      renderCell: (params) => {
        const licensePlate = params.value || "-";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontFamily: "Outfit, sans-serif" }}>
              {licensePlate}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyVehicleNumber(licensePlate);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                borderRadius: "4px",
                color: "#A8A29E",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#F5F5F4";
                e.currentTarget.style.color = "#4F46E5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#A8A29E";
              }}
              title="Copy vehicle number"
              aria-label="Copy vehicle number"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 200,
      flex: 1,
      align: "start",
      renderCell: (params) => {
        const status = params.value;
        let color = "#78716C";
        let bgColor = "#F5F5F4";
        let dotColor = "#78716C";

        if (status === "High Profit") {
          color = "#15803D";
          bgColor = "#DCFCE7";
          dotColor = "#15803D";
        } else if (status === "Moderate Profit") {
          color = "#22C55E";
          bgColor = "#F0FDF4";
          dotColor = "#22C55E";
        } else if (status === "Low Profit") {
          color = "#65A30D";
          bgColor = "#F7FEE7";
          dotColor = "#65A30D";
        } else if (status === "Verge Of Loss") {
          color = "#D97706";
          bgColor = "#FFFBEB";
          dotColor = "#D97706";
        } else if (status === "Loss") {
          color = "#DC2626";
          bgColor = "#FEF2F2";
          dotColor = "#DC2626";
        }

        return (
          <div
            style={{
              background: bgColor,
              color: color,
              display: "inline-flex",
              padding: "2px 10px",
              borderRadius: 6,
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              fontWeight: "500",
              border: "0.80px #ECECEC solid",
              fontFamily: "Outfit, sans-serif",
            }}
          >
            <span style={{ color: dotColor, fontSize: 8 }}>●</span>
            {status}
          </div>
        );
      },
    },

    {
      field: "totalBookings",
      headerName: "Total Bookings",
      minWidth: 200,
      flex: 1,
      align: "start",
      sortable: false,
    },
    {
      field: "peakBookingDays",
      headerName: "Peak Booking Days",
      minWidth: 200,
      flex: 1,
      align: "start",
      sortable: false,
    },
    {
      field: "revenue",
      headerName: "Revenue",
      minWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <span style={{ fontFamily: "Outfit, sans-serif" }}>
          ₹{Number(params.value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "maintenance",
      headerName: "Maintenance",
      minWidth: 200,
      flex: 1,
      align: "start",
      renderCell: (params) => (
        <span style={{ fontFamily: "Outfit, sans-serif" }}>
          ₹{Number(params.value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "fuel",
      headerName: "Fuel",
      minWidth: 200,
      flex: 1,
      align: "start",
      renderCell: (params) => (
        <span style={{ fontFamily: "Outfit, sans-serif" }}>
          ₹{Number(params.value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "total",
      headerName: "Total Maintenance Cost",
      minWidth: 200,
      flex: 1,
      align: "start",
      renderCell: (params) => (
        <span style={{ fontFamily: "Outfit, sans-serif" }}>
          ₹{Number(params.value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "netProfit",
      headerName: "Net Profit",
      minWidth: 200,
      flex: 1,
      align: "start",
      renderCell: (params) => (
        <span
          style={{
            fontFamily: "Outfit, sans-serif",
            fontWeight: 500,
            color: Number(params.value) > 0 ? "#16A34A" : "#DC2626",
          }}
        >
          ₹{Number(params.value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "revenuePerDay",
      headerName: "Revenue Per Day",
      minWidth: 200,
      flex: 1,
      align: "start",
      renderCell: (params) => (
        <span style={{ fontFamily: "Outfit, sans-serif" }}>
          ₹{Number(params.value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "costPerDay",
      headerName: "Cost Per Day",
      minWidth: 200,
      flex: 1,
      align: "start",
      renderCell: (params) => (
        <span style={{ fontFamily: "Outfit, sans-serif" }}>
          ₹{Number(params.value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      field: "breakEvenDays",
      headerName: "Break Even Days",
      minWidth: 200,
      flex: 1,
      align: "start",
      renderCell: (params) => (
        <span style={{ fontFamily: "Outfit, sans-serif" }}>
          {Number(params.value || 0).toFixed(0)} days
        </span>
      ),
    },
  ];

  // Initialize visible columns after cols is defined
  useEffect(() => {
    setColumns(cols);
    // Initialize all columns as visible
    const initialVisibility = {};
    cols.forEach((col) => {
      initialVisibility[col.field] = true;
    });
    setVisibleColumns(initialVisibility);
  }, []);
  const rows =
    vehicleTrendGridApi &&
    vehicleTrendGridApi.map((item) =>
      createData(
        item?.id,
        item?.name || "-",
        item?.licensePlate || "-",
        item?.analytics?.totalBookings ?? 0,
        item?.analytics?.peakBookingDays ?? [],
        item?.analytics?.costingAndProfitability?.revenue ?? 0,
        item?.analytics?.costingAndProfitability?.costs?.maintenance ?? 0,
        item?.analytics?.costingAndProfitability?.costs?.fuel ?? 0,
        item?.analytics?.costingAndProfitability?.costs?.total ?? 0,
        item?.analytics?.costingAndProfitability?.netProfit ?? 0,
        item?.analytics?.costingAndProfitability?.revenuePerDay ?? 0,
        item?.analytics?.costingAndProfitability?.costPerDay ?? 0,
        item?.analytics?.costingAndProfitability?.breakEvenDays ?? 0,
        item?.analytics?.costingAndProfitability?.status
          ?.toLowerCase()
          .replaceAll("_", " ")
          .replace(/\b\w/g, (char) => char.toUpperCase()) || "-",
      ),
    );
  // Filter and search logic
  const filteredRows = (rows || []).filter((row) => {
    // Search query filter
    let matchesSearch = true;
    if (searchQuery) {
      matchesSearch = Object.values(row).some((val) =>
        String(val).toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Column filters
    let matchesColumnFilters = true;
    for (const [field, filter] of Object.entries(columnFilters)) {
      if (!filter || !filter.value) continue;

      const cellValue = String(row[field] ?? "").toLowerCase();
      const filterValue = String(filter.value).toLowerCase();

      switch (filter.operator) {
        case "contains":
          if (!cellValue.includes(filterValue)) matchesColumnFilters = false;
          break;
        case "notContains":
          if (cellValue.includes(filterValue)) matchesColumnFilters = false;
          break;
        case "equals":
          if (cellValue !== filterValue) matchesColumnFilters = false;
          break;
        case "startsWith":
          if (!cellValue.startsWith(filterValue)) matchesColumnFilters = false;
          break;
        case "endsWith":
          if (!cellValue.endsWith(filterValue)) matchesColumnFilters = false;
          break;
        case "isEmpty":
          if (cellValue !== "") matchesColumnFilters = false;
          break;
        case "isNotEmpty":
          if (cellValue === "") matchesColumnFilters = false;
          break;
        default:
          break;
      }

      if (!matchesColumnFilters) break;
    }

    return matchesSearch && matchesColumnFilters;
  });

  // Apply pagination
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = startIndex + paginationModel.pageSize;
  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  // Get visible columns for rendering
  const visibleColumnsList = columns.filter((col) => visibleColumns[col.field]);

  // Export functions
  const getTableHeaders = () => {
    const headers = ["S.No"];
    visibleColumnsList.forEach((col) => {
      headers.push(col.headerName);
    });
    return headers;
  };

  const getTableData = () => {
    const data = [];
    filteredRows.forEach((row, idx) => {
      const rowData = [String(idx + 1)];
      visibleColumnsList.forEach((col) => {
        let value = row[col.field] ?? "-";
        if (typeof value === "object") {
          value = JSON.stringify(value);
        }
        if (
          col.field === "revenue" ||
          col.field === "maintenance" ||
          col.field === "fuel" ||
          col.field === "total" ||
          col.field === "netProfit" ||
          col.field === "revenuePerDay" ||
          col.field === "costPerDay"
        ) {
          value = `₹${Number(value).toFixed(2)}`;
        }
        if (col.field === "breakEvenDays") {
          value = `${Number(value).toFixed(0)} days`;
        }
        if (col.field === "peakBookingDays" && Array.isArray(value)) {
          value = value.join(", ");
        }
        rowData.push(String(value));
      });
      data.push(rowData);
    });
    return data;
  };

  const exportToCSV = () => {
    const headers = getTableHeaders();
    const data = getTableData();

    const csvContent = [headers, ...data]
      .map((row) =>
        row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Vehicle_Trends_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const headers = getTableHeaders();
    const data = getTableData();

    const printWindow = window.open("", "_blank");
    const currentDate = new Date().toLocaleString();

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Vehicle Trends Report - ${currentDate}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Outfit', 'Segoe UI', Arial, sans-serif; padding: 40px; background: white; }
          .report-header { margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #E7E5E0; }
          .report-title { font-size: 24px; font-weight: 600; color: #1A1917; margin-bottom: 8px; }
          .report-subtitle { font-size: 14px; color: #78716C; margin-bottom: 8px; }
          .report-meta { font-size: 12px; color: #A8A29E; margin-top: 8px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #F5F5F4; color: #1A1917; font-weight: 600; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; padding: 12px; text-align: left; border: 1px solid #E7E5E0; }
          td { padding: 10px 12px; border: 1px solid #E7E5E0; color: #57534E; font-size: 13px; }
          tr:hover { background-color: #FAFAFA; }
          .status-badge { display: inline-block; padding: 4px 8px; border-radius: 6px; font-size: 11px; font-weight: 500; }
          @media print {
            body { padding: 20px; }
            th { background: #F5F5F4 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="report-title">Vehicle Trends Report</div>
          <div class="report-subtitle">Analytics and performance metrics for fleet vehicles</div>
          <div class="report-meta">Generated on: ${currentDate} | Total Records: ${filteredRows.length} | City: ${city || "All"}</div>
        </div>
        <table>
          <thead>
            <tr>${headers.map((header) => `<th>${header}</th>`).join("")}</tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row) => `
              <tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>
            `,
              )
              .join("")}
          </tbody>
        </table>
        <div style="margin-top: 30px; text-align: center; font-size: 11px; color: #A8A29E;">
          This report was generated automatically. For support, contact your system administrator.
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleDownloadClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportOption = (option) => {
    if (option === "print") {
      handlePrint();
    } else if (option === "csv") {
      exportToCSV();
    }
    handleClose();
  };

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const addFilter = (field, operator, value) => {
    const newFilter = {
      field,
      operator,
      value,
      columnName: columns.find((c) => c.field === field)?.headerName,
    };
    setColumnFilters((prev) => ({ ...prev, [field]: { operator, value } }));
    setActiveFilters((prev) => [...prev, newFilter]);
    handleFilterClose();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const removeFilter = (field) => {
    const newFilters = { ...columnFilters };
    delete newFilters[field];
    setColumnFilters(newFilters);
    setActiveFilters(activeFilters.filter((f) => f.field !== field));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setActiveFilters([]);
    setSearchQuery("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Column visibility handlers
  const handleColumnClick = (event) => {
    setColumnAnchorEl(event.currentTarget);
  };

  const handleColumnClose = () => {
    setColumnAnchorEl(null);
  };

  const toggleColumn = (field) => {
    setVisibleColumns((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const showAllColumns = () => {
    const allVisible = {};
    columns.forEach((col) => {
      allVisible[col.field] = true;
    });
    setVisibleColumns(allVisible);
  };

  const hideAllColumns = () => {
    const allHidden = {};
    columns.forEach((col) => {
      allHidden[col.field] = false;
    });
    setVisibleColumns(allHidden);
  };

  useEffect(() => {
    if (!city) {
      setVehicleTrendGridApi([]);
      return;
    }

    getVehicleTrendData(city);
  }, [vehicleId]);

  const filteredTotalCount = filteredRows.length;
  const totalPages =
    Math.ceil(filteredTotalCount / paginationModel.pageSize) || 1;

  // Reset to first page when search query or filters change
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchQuery, columnFilters]);

  if (!city) return null;

  return (
    <div
      style={{
        width: "100%",
        background: "white",
        boxShadow:
          "0px 1px 2px rgba(0, 0, 0, 0.03), 0px 1px 3px rgba(0, 0, 0, 0.04)",
        overflow: "hidden",
        borderRadius: 16,
        outline: "0.80px #E7E5E0 solid",
        outlineOffset: "-0.80px",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        display: "inline-flex",
        fontFamily: "Outfit, sans-serif",
        margin: "30px 0",
      }}
    >
      {/* HEADER SECTION METADATA */}
      <div
        style={{
          alignSelf: "stretch",
          paddingLeft: 28,
          paddingRight: 28,
          paddingTop: 20,
          paddingBottom: 20,
          borderBottom: "0.80px #EEEDE9 solid",
          justifyContent: "space-between",
          alignItems: "center",
          display: "inline-flex",
        }}
      >
        <div
          style={{
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 16,
            display: "flex",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              background: "#F0EFE9",
              borderRadius: 16,
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <div
              style={{
                width: 15,
                height: 15,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: 12.5,
                  height: 6.25,
                  left: 1.25,
                  top: 4.38,
                  position: "absolute",
                  outline: "1.25px #4F46E5 solid",
                  outlineOffset: "-0.63px",
                }}
              />
              <div
                style={{
                  width: 2.5,
                  height: 2.5,
                  left: 3.13,
                  top: 9.38,
                  position: "absolute",
                  outline: "1.25px #4F46E5 solid",
                  outlineOffset: "-0.63px",
                }}
              />
              <div
                style={{
                  width: 2.5,
                  height: 2.5,
                  left: 9.38,
                  top: 9.38,
                  position: "absolute",
                  outline: "1.25px #4F46E5 solid",
                  outlineOffset: "-0.63px",
                }}
              />
            </div>
          </div>
          <div
            style={{
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              display: "inline-flex",
            }}
          >
            <div
              style={{
                color: "#1A1917",
                fontSize: 16,
                fontWeight: "600",
                lineHeight: "19.2px",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              Vehicle Trends
            </div>
            <div
              style={{
                paddingTop: 2,
                color: "#A8A29E",
                fontSize: 13,
                fontWeight: "400",
                lineHeight: "19.5px",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              Analytics and performance metrics for fleet vehicles
            </div>
          </div>
        </div>
        <div
          style={{
            paddingLeft: 12,
            paddingRight: 12,
            height: 30,
            background: "#F0EFE9",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              color: "#78716C",
              fontSize: 12,
              fontWeight: "500",
              fontFamily: "Outfit, sans-serif",
            }}
          >
            {filteredTotalCount} vehicles
          </span>
        </div>
      </div>

      {/* FILTER CONTROL BAR STRIP - Same as before */}
      <div
        style={{
          alignSelf: "stretch",
          paddingLeft: 28,
          paddingRight: 28,
          paddingTop: 16,
          paddingBottom: 16,
          borderBottom: "0.80px #EEEDE9 solid",
          justifyContent: "space-between",
          alignItems: "center",
          display: "inline-flex",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
            flex: 1,
          }}
        >
          {/* Search Input */}
          <div
            style={{
              width: 256,
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 10,
              paddingBottom: 10,
              background: "white",
              borderRadius: 12,
              outline: "0.80px #E7E5E0 solid",
              outlineOffset: "-0.80px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 13,
                height: 13,
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="5.5"
                  cy="5.5"
                  r="4"
                  stroke="#A8A29E"
                  strokeWidth="1.2"
                />
                <path
                  d="M8.5 8.5L11.5 11.5"
                  stroke="#A8A29E"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search vehicles..."
              style={{
                width: "100%",
                border: "none",
                outline: "none",
                color: "#1A1917",
                fontSize: 14,
                fontWeight: "400",
                fontFamily: "Outfit, sans-serif",
              }}
            />
          </div>

          {/* Filter Button */}
          <Button
            onClick={handleFilterClick}
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{
              textTransform: "none",
              fontFamily: "Outfit, sans-serif",
              borderRadius: "12px",
              padding: "6px 16px",
              fontSize: "13px",
              fontWeight: "500",
              borderColor: "#E7E5E0",
              color: "#1A1917",
              "&:hover": {
                borderColor: "#4F46E5",
                backgroundColor: "#F5F3FF",
              },
            }}
          >
            Filter
          </Button>

          {/* Column Visibility Button */}
          <Button
            onClick={handleColumnClick}
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            sx={{
              textTransform: "none",
              fontFamily: "Outfit, sans-serif",
              borderRadius: "12px",
              padding: "6px 16px",
              fontSize: "13px",
              fontWeight: "500",
              borderColor: "#E7E5E0",
              color: "#1A1917",
              "&:hover": {
                borderColor: "#4F46E5",
                backgroundColor: "#F5F3FF",
              },
            }}
          >
            Columns
          </Button>

          {/* Active Filters Chips */}
          {activeFilters.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {activeFilters.map((filter) => (
                <Chip
                  key={filter.field}
                  label={`${filter.columnName} ${filter.operator} ${filter.value || ""}`}
                  onDelete={() => removeFilter(filter.field)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontFamily: "Outfit, sans-serif",
                    fontSize: "12px",
                    height: "28px",
                    backgroundColor: "#F5F5F4",
                    "& .MuiChip-label": { padding: "0 8px" },
                    "& .MuiChip-deleteIcon": {
                      fontSize: "14px",
                      marginRight: "4px",
                    },
                  }}
                />
              ))}
              <button
                onClick={clearAllFilters}
                style={{
                  background: "none",
                  border: "none",
                  color: "#EF4444",
                  fontSize: "12px",
                  fontFamily: "Outfit, sans-serif",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Download Button */}
        <div>
          <Button
            onClick={handleDownloadClick}
            variant="contained"
            startIcon={<FileDownloadIcon />}
            sx={{
              textTransform: "none",
              fontFamily: "Outfit, sans-serif",
              background: "#1A1917",
              borderRadius: "12px",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: "500",
              "&:hover": {
                background: "#2D2A26",
              },
            }}
          >
            Download Report
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: "12px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                minWidth: "180px",
                "& .MuiMenuItem-root": {
                  fontFamily: "Outfit, sans-serif",
                  fontSize: "13px",
                  padding: "8px 16px",
                  gap: "12px",
                },
              },
            }}
          >
            <MenuItem onClick={() => handleExportOption("print")}>
              <PrintIcon sx={{ fontSize: "18px", color: "#78716C" }} />
              <span>Print Report</span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "11px",
                  color: "#A8A29E",
                }}
              >
                Ctrl+P
              </span>
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("csv")}>
              <DescriptionIcon sx={{ fontSize: "18px", color: "#78716C" }} />
              <span>Export as CSV</span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "11px",
                  color: "#A8A29E",
                }}
              >
                .csv
              </span>
            </MenuItem>
          </Menu>
        </div>
      </div>

      {/* Filter Popover */}
      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "16px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            width: "400px",
            p: 2,
            fontFamily: "Outfit, sans-serif",
          },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1A1917",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              Add Filter
            </span>
            <IconButton size="small" onClick={handleFilterClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontFamily: "Outfit, sans-serif" }}>
              Column
            </InputLabel>
            <Select
              value={selectedFilterColumn}
              onChange={(e) => setSelectedFilterColumn(e.target.value)}
              label="Column"
              sx={{ fontFamily: "Outfit, sans-serif", borderRadius: "12px" }}
            >
              {columns.map((col) => (
                <MenuItem
                  key={col.field}
                  value={col.field}
                  sx={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {col.headerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontFamily: "Outfit, sans-serif" }}>
              Operator
            </InputLabel>
            <Select
              value={selectedOperator}
              onChange={(e) => setSelectedOperator(e.target.value)}
              label="Operator"
              sx={{ fontFamily: "Outfit, sans-serif", borderRadius: "12px" }}
            >
              {operators.map((op) => (
                <MenuItem
                  key={op.value}
                  value={op.value}
                  sx={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedOperator !== "isEmpty" &&
            selectedOperator !== "isNotEmpty" && (
              <TextField
                size="small"
                label="Value"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                fullWidth
                sx={{
                  "& .MuiInputLabel-root": { fontFamily: "Outfit, sans-serif" },
                  "& .MuiInputBase-root": {
                    fontFamily: "Outfit, sans-serif",
                    borderRadius: "12px",
                  },
                }}
              />
            )}

          <Button
            variant="contained"
            onClick={() => {
              if (selectedFilterColumn) {
                addFilter(selectedFilterColumn, selectedOperator, filterValue);
                setSelectedFilterColumn("");
                setSelectedOperator("contains");
                setFilterValue("");
              }
            }}
            disabled={
              !selectedFilterColumn ||
              (selectedOperator !== "isEmpty" &&
                selectedOperator !== "isNotEmpty" &&
                !filterValue)
            }
            sx={{
              textTransform: "none",
              fontFamily: "Outfit, sans-serif",
              background: "#4F46E5",
              borderRadius: "12px",
              "&:hover": { background: "#4338CA" },
            }}
          >
            Apply Filter
          </Button>
        </div>
      </Popover>

      {/* Column Visibility Popover */}
      <Popover
        open={columnOpen}
        anchorEl={columnAnchorEl}
        onClose={handleColumnClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "16px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            width: "280px",
            p: 2,
            fontFamily: "Outfit, sans-serif",
          },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1A1917",
                fontFamily: "Outfit, sans-serif",
              }}
            >
              Show/Hide Columns
            </span>
            <IconButton size="small" onClick={handleColumnClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              borderBottom: "0.80px solid #EEEDE9",
              paddingBottom: 8,
            }}
          >
            <button
              onClick={showAllColumns}
              style={{
                background: "none",
                border: "none",
                color: "#4F46E5",
                fontSize: "12px",
                fontFamily: "Outfit, sans-serif",
                cursor: "pointer",
              }}
            >
              Show All
            </button>
            <span style={{ color: "#E7E5E0" }}>|</span>
            <button
              onClick={hideAllColumns}
              style={{
                background: "none",
                border: "none",
                color: "#4F46E5",
                fontSize: "12px",
                fontFamily: "Outfit, sans-serif",
                cursor: "pointer",
              }}
            >
              Hide All
            </button>
          </div>

          <div style={{ maxHeight: "300px", overflowY: "auto" }}>
            {columns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={visibleColumns[col.field] === true}
                    onChange={() => toggleColumn(col.field)}
                    sx={{
                      color: "#A8A29E",
                      "&.Mui-checked": { color: "#4F46E5" },
                    }}
                  />
                }
                label={col.headerName}
                sx={{
                  width: "100%",
                  margin: 0,
                  padding: "8px 0",
                  "& .MuiFormControlLabel-label": {
                    fontFamily: "Outfit, sans-serif",
                    fontSize: "13px",
                    color: "#1A1917",
                  },
                }}
              />
            ))}
          </div>
        </div>
      </Popover>

      {/* SPREADSHEET TABLE GRID CONTAINER */}
      <div
        style={{
          alignSelf: "stretch",
          overflowX: "auto",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            minWidth:
              visibleColumnsList.length > 0
                ? 60 + visibleColumnsList.length * 140
                : 300,
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* TABLE HEADERS */}
          <div
            style={{
              height: 43.4,
              background: "#FDFDFD",
              borderBottom: "0.80px #EEEDE9 solid",
              boxShadow: "0px 2px 3px rgba(0, 0, 0, 0.02)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 60,
                paddingLeft: 20,
                color: "#A8A29E",
                fontSize: 10,
                fontWeight: "500",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                fontFamily: "Outfit, sans-serif",
              }}
            >
              S.No
            </div>
            {visibleColumnsList.map((col, idx) => (
              <div
                key={col.field || idx}
                style={{
                  flex: col.flex || 1,
                  minWidth: col.width || 120,
                  paddingLeft: 20,
                  color: "#A8A29E",
                  fontSize: 10,
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontFamily: "Outfit, sans-serif",
                }}
              >
                {col.headerName}
              </div>
            ))}
          </div>

          {/* TABLE ROWS */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {loading ? (
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: 270,
                }}
              >
                <CircularProgress size={30} style={{ color: "#4F46E5" }} />
              </Box>
            ) : paginatedRows.length === 0 ? (
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: 270,
                }}
              >
                <Typography
                  style={{
                    color: "#A8A29E",
                    fontSize: 14,
                    fontFamily: "Outfit, sans-serif",
                  }}
                >
                  {searchQuery || activeFilters.length > 0
                    ? "No matching vehicles found"
                    : "No vehicle data found"}
                </Typography>
              </Box>
            ) : (
              paginatedRows.map((row, index) => {
                const globalIndex = startIndex + index + 1;
                return (
                  <div
                    key={row.id || index}
                    style={{
                      alignSelf: "stretch",
                      height: 54,
                      background: "white",
                      borderBottom: "0.80px #F0EFE9 solid",
                      display: "flex",
                      alignItems: "center",
                    }}
                    className="hover:bg-stone-50/60 transition-colors"
                  >
                    <div
                      style={{
                        width: 60,
                        paddingLeft: 20,
                        color: "#C8C4BE",
                        fontSize: 14,
                        fontWeight: "400",
                        fontFamily: "Outfit, sans-serif",
                      }}
                    >
                      {globalIndex}
                    </div>
                    {visibleColumnsList.map((col, colIdx) => {
                      let cellValue = row[col.field] ?? "-";

                      // Special formatting for certain fields
                      if (
                        col.field === "revenue" ||
                        col.field === "maintenance" ||
                        col.field === "fuel" ||
                        col.field === "total" ||
                        col.field === "netProfit" ||
                        col.field === "revenuePerDay" ||
                        col.field === "costPerDay"
                      ) {
                        cellValue = `₹${Number(cellValue).toFixed(2)}`;
                      }
                      if (col.field === "breakEvenDays") {
                        cellValue = `${Number(cellValue).toFixed(0)} days`;
                      }
                                            if (
                        col.field === "peakBookingDays" &&
                        Array.isArray(cellValue)
                      ) {
                        const joinedValue = cellValue.join(", ");
                        // Truncate if too long (show first 3 days + count)
                        if (cellValue.length > 3) {
                          cellValue = `${cellValue.slice(0, 3).join(", ")} +${cellValue.length - 3} more`;
                        } else {
                          cellValue = joinedValue;
                        }
                      }

                      // Check if this field has a custom renderCell
                      if (col.renderCell) {
                        return (
                          <div
                            key={col.field || colIdx}
                            style={{
                              flex: col.flex || 1,
                              minWidth: col.width || 120,
                              paddingLeft: col.field === "licensePlate" ? 0 : 20,
                              paddingRight: col.field === "licensePlate" ? 8 : 0,
                              fontFamily: "Outfit, sans-serif",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {col.renderCell({ value: row[col.field] })}
                          </div>
                        );
                      }

                      return (
                        <div
                          key={col.field || colIdx}
                          style={{
                            flex: col.flex || 1,
                            minWidth: col.width || 120,
                            paddingLeft: 20,
                            color: "#57534E",
                            fontSize: col.field === "peakBookingDays" ? 13 : 14,
                            fontWeight:
                              col.field === "netProfit" ? "500" : "400",
                            fontFamily: "Outfit, sans-serif",
                            ...(col.field === "peakBookingDays" && {
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              lineHeight: "1.4",
                            }),
                          }}
                        >
                          {cellValue}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* PAGINATION FOOTER */}
      <div
        style={{
          alignSelf: "stretch",
          paddingLeft: 28,
          paddingRight: 28,
          paddingTop: 14,
          paddingBottom: 14,
          borderTop: "0.80px #EEEDE9 solid",
          justifyContent: "space-between",
          alignItems: "center",
          display: "inline-flex",
          background: "white",
        }}
      >
        <div
          style={{
            color: "#A8A29E",
            fontSize: 13,
            fontWeight: "400",
            fontFamily: "Outfit, sans-serif",
          }}
        >
          Showing {filteredTotalCount === 0 ? 0 : startIndex + 1}–
          {Math.min(endIndex, filteredTotalCount)} of {filteredTotalCount}{" "}
          vehicles
        </div>

        {/* PAGINATION CONTROLS */}
        <div
          style={{
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 4,
            display: "flex",
          }}
        >
          <button
            type="button"
            disabled={paginationModel.page === 0}
            onClick={() =>
              setPaginationModel((prev) => ({
                ...prev,
                page: Math.max(0, prev.page - 1),
              }))
            }
            style={{
              width: 32,
              height: 32,
              background: "white",
              borderRadius: 12,
              border: "0.80px #E7E5E0 solid",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: paginationModel.page === 0 ? "not-allowed" : "pointer",
              opacity: paginationModel.page === 0 ? 0.3 : 1,
              fontFamily: "Outfit, sans-serif",
            }}
            aria-label="Previous page"
          >
            ‹
          </button>

          {Array.from({ length: Math.min(totalPages, 5) }).map((_, pageIdx) => {
            let displayPage = pageIdx;
            if (totalPages > 5) {
              if (paginationModel.page > 2) {
                displayPage = paginationModel.page - 2 + pageIdx;
                if (displayPage >= totalPages) return null;
              }
            }

            const isCurrentPage = paginationModel.page === displayPage;
            if (displayPage < totalPages) {
              return (
                <button
                  key={displayPage}
                  type="button"
                  onClick={() =>
                    setPaginationModel((prev) => ({
                      ...prev,
                      page: displayPage,
                    }))
                  }
                  style={{
                    width: 32,
                    height: 32,
                    background: isCurrentPage ? "#1A1917" : "white",
                    borderRadius: 12,
                    border: isCurrentPage
                      ? "0.80px #1A1917 solid"
                      : "0.80px #E7E5E0 solid",
                    color: isCurrentPage ? "white" : "#57534E",
                    fontSize: 12,
                    fontWeight: "500",
                    cursor: "pointer",
                    fontFamily: "Outfit, sans-serif",
                  }}
                >
                  {displayPage + 1}
                </button>
              );
            }
            return null;
          })}

          <button
            type="button"
            disabled={paginationModel.page + 1 >= totalPages}
            onClick={() =>
              setPaginationModel((prev) => ({
                ...prev,
                page: Math.min(totalPages - 1, prev.page + 1),
              }))
            }
            style={{
              width: 32,
              height: 32,
              background: "white",
              borderRadius: 12,
              border: "0.80px #E7E5E0 solid",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor:
                paginationModel.page + 1 >= totalPages
                  ? "not-allowed"
                  : "pointer",
              opacity: paginationModel.page + 1 >= totalPages ? 0.3 : 1,
              fontFamily: "Outfit, sans-serif",
            }}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleTrendGrid;
